import CloseIcon from '@mui/icons-material/Close';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import LinearProgress from '@mui/material/LinearProgress';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import {
  QueryFilters,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import { API } from 'aws-amplify';
import React from 'react';
import isEmail from 'validator/lib/isEmail';
import { GHLSearchData } from '../../../types';
import { getErrorMessage } from '../../../utils/error-message';

export function GHLUpdate(props: {
  queryKey: QueryFilters['queryKey'];
  currentEmail: string | null;
  onClose: () => void;
}) {
  // State
  const [Email, setEmail] = React.useState('');
  // Hooks
  const queryClient = useQueryClient();

  React.useEffect(() => {
    if (props.currentEmail) {
      setEmail(props.currentEmail);
    }
  }, [props.currentEmail]);

  // Mutation - Ppdate user email address for Go High Level
  const mutation = useMutation({
    mutationFn: async () => {
      await API.post('ContractingAPI', '/go-high-level/update', {
        body: {
          Email,
        },
      });
    },
    onSuccess: async () => {
      // Update /search for account in Go High Level
      if (props.queryKey) {
        queryClient.setQueryData<GHLSearchData>(props.queryKey, (oldData) => {
          if (oldData) {
            return {
              ...oldData,
              email: Email,
            };
          } else {
            return {
              id: '',
              firstName: '',
              lastName: '',
              source: '',
              phone: '',
              email: '',
              country: '',
              state: '',
              postalCode: '',
              city: '',
              tags: [],
            };
          }
        });
      }

      // Close form
      props.onClose();
    },
  });

  const handleClose = () => {
    if (!mutation.isLoading) {
      props.onClose();
    }
  };

  const submitDisabled = !Email || !isEmail(Email);

  return (
    <Dialog open onClose={handleClose}>
      <DialogTitle>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Box>Update</Box>

          <IconButton
            disabled={mutation.isLoading}
            size="small"
            aria-label="close"
            onClick={handleClose}
          >
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={1}>
          <DialogContentText>
            Update the email address used for Go High Level.
          </DialogContentText>
          <TextField
            id="ghl-update-Email"
            autoFocus
            required
            fullWidth
            variant="outlined"
            label="Email Address"
            type="email"
            value={Email}
            onChange={(event) => setEmail(event.target.value)}
          />
        </Stack>
      </DialogContent>

      {mutation.isError ? (
        <Alert
          onClose={() => mutation.reset()}
          severity="error"
          sx={{ borderRadius: 0 }}
        >
          <strong style={{ marginRight: 10 }}>An error has occurred!</strong>
          <small>
            {getErrorMessage({
              error: mutation.error,
              fallback: 'Please try again.',
            })}
          </small>
        </Alert>
      ) : null}

      {mutation.isLoading ? <LinearProgress /> : null}

      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button
          disabled={mutation.isLoading || submitDisabled}
          onClick={() => {
            mutation.mutate();
          }}
        >
          {mutation.isLoading ? 'Updating...' : 'Update Email'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
